import React, { useState, useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Menu,
  Container,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme,
  Fade,
  Slide,
  Zoom,
  alpha,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";

const logoSrc = `${process.env.PUBLIC_URL}/assets/images/CompanyLogo4.png`;
const pages = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Products & Solutions", path: "/products" },
  { name: "Leadership", path: "/leadership" },
  { name: "Roadmap", path: "/roadmap" },
  { name: "Contact Us", path: "/contact" },
];

function Header() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Animation on mount
    setMounted(true);

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  // Check if current page
  const isCurrentPage = (path) => {
    return location.pathname === path;
  };

  return (
    <Slide appear={false} direction="down" in={mounted}>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: scrolled
            ? "background.paper"
            : alpha(theme.palette.background.paper, 0.9),
          transition: "all 0.3s ease-in-out",
          boxShadow: scrolled ? 3 : 0,
          backdropFilter: "blur(8px)",
          width: "100%",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              flexDirection: { xs: "row" },
              justifyContent: { xs: "space-between" },
              width: "100%",
            }}
          >
            {/* Logo for large screens */}
            <Zoom in={mounted} timeout={800}>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  mr: 2,
                  textDecoration: "none",
                  flexShrink: 0,
                }}
              >
                <img
                  src={logoSrc}
                  alt="ProtoDiag Solutions Logo"
                  style={{
                    height: "70px",
                    width: "auto",
                    maxWidth: "200px",
                    objectFit: "contain",
                    transition: "transform 0.3s ease",
                    transform: scrolled ? "scale(0.9)" : "scale(1)",
                  }}
                />
              </Box>
            </Zoom>

            {/* Mobile menu icon */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                order: { xs: 1, sm: 1 },
              }}
            >
              <IconButton
                size="large"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="primary"
                sx={{
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "scale(1.1)",
                  },
                }}
              >
                {Boolean(anchorElNav) ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: "block", md: "none" },
                  "& .MuiMenu-paper": {
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    backdropFilter: "blur(10px)",
                    width: { xs: "100%", sm: "auto" },
                    maxWidth: { xs: "100%", sm: "400px" },
                    left: { xs: "0 !important", sm: "auto" },
                    right: { xs: "0 !important", sm: "auto" },
                  },
                }}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 400 }}
                PaperProps={{
                  style: {
                    maxHeight: "80vh",
                    width: isSmall ? "100vw" : "auto",
                    marginTop: "8px",
                    marginLeft: isSmall ? "0" : undefined,
                  },
                }}
              >
                {/* Mobile menu items */}
                {pages.map((page, index) => (
                  <Fade
                    in={Boolean(anchorElNav)}
                    timeout={300 + index * 100}
                    key={page.name}
                  >
                    <MenuItem
                      onClick={handleCloseNavMenu}
                      component={RouterLink}
                      to={page.path}
                      sx={{
                        backgroundColor: isCurrentPage(page.path)
                          ? alpha(theme.palette.primary.main, 0.1)
                          : "transparent",
                        borderLeft: isCurrentPage(page.path)
                          ? `4px solid ${theme.palette.primary.main}`
                          : "4px solid transparent",
                        my: 0.5,
                        mx: 1,
                        borderRadius: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          ),
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      {page.name}
                    </MenuItem>
                  </Fade>
                ))}
                {/* Mobile CTA button */}
                {isMobile && (
                  <Fade
                    in={Boolean(anchorElNav)}
                    timeout={300 + pages.length * 100}
                  >
                    <MenuItem
                      component={RouterLink}
                      to="/contact"
                      onClick={handleCloseNavMenu}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        my: 1.5,
                        mx: 1,
                        borderRadius: 1,
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.9
                          ),
                        },
                      }}
                    >
                      Get in Touch
                    </MenuItem>
                  </Fade>
                )}
              </Menu>
            </Box>

            {/* Logo for mobile */}
            <Zoom in={mounted} timeout={800}>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: { xs: "flex", md: "none" },
                  justifyContent: "center",
                  alignItems: "center",
                  textDecoration: "none",
                  order: { xs: 2, sm: 2 },
                  flex: { xs: "1 1 auto" },
                  position: "relative",
                }}
              >
                <img
                  src={logoSrc}
                  alt="ProtoDiag Logo"
                  style={{
                    height: "60px",
                    width: "auto",
                    maxWidth: "160px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Zoom>

            {/* Placeholder for layout balance on mobile */}
            <Box
              sx={{
                display: { xs: "block", md: "none" },
                width: "48px", // Same width as the menu button
                order: { xs: 3, sm: 3 },
              }}
            />

            {/* Desktop menu */}
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: "none", md: "flex" },
                justifyContent: "flex-end",
                flexWrap: "nowrap",
                overflow: "auto",
              }}
            >
              {pages.map((page, index) => (
                <Fade in={mounted} timeout={800 + index * 100} key={page.name}>
                  <Button
                    component={RouterLink}
                    to={page.path}
                    onClick={handleCloseNavMenu}
                    sx={{
                      my: 2,
                      mx: 0.5,
                      color: isCurrentPage(page.path)
                        ? "primary.main"
                        : "text.primary",
                      display: "block",
                      fontFamily: "Roboto Slab, serif",
                      fontWeight: 500,
                      letterSpacing: 1,
                      position: "relative",
                      whiteSpace: "nowrap",
                      minWidth: "auto",
                      px: 1.5,
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        width: isCurrentPage(page.path) ? "80%" : "0%",
                        height: "2px",
                        bottom: "10px",
                        left: "10%",
                        backgroundColor: "primary.main",
                        transition: "width 0.3s ease",
                      },
                      "&:hover": {
                        color: "primary.main",
                        backgroundColor: "transparent",
                        "&::after": {
                          width: "80%",
                        },
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {page.name}
                  </Button>
                </Fade>
              ))}
            </Box>

            {/* CTA button for desktop */}
            {!isMobile && (
              <Zoom in={mounted} timeout={1200}>
                <Button
                  variant="contained"
                  color="primary"
                  component={RouterLink}
                  to="/contact"
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    ml: 2,
                    fontFamily: "Roboto Slab, serif",
                    fontWeight: 600,
                    px: { md: 2, lg: 3 },
                    py: 1,
                    borderRadius: 2,
                    whiteSpace: "nowrap",
                    transition: "all 0.3s ease",
                    boxShadow: theme.shadows[4],
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: theme.shadows[8],
                      backgroundColor: alpha(theme.palette.primary.main, 0.9),
                    },
                    "&:active": {
                      transform: "translateY(0)",
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  Get in Touch
                </Button>
              </Zoom>
            )}
          </Toolbar>
        </Container>
      </AppBar>
    </Slide>
  );
}

export default Header;
